import { Box, Collapse, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useMemo, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
	APPROVAL_ACTIONS,
	COMPANY_APPROVAL_TYPES,
	FACTORY_APPROVAL_TYPES,
	getApprovalHistory,
	getApprovalStatus,
	getLatestApproval,
} from "utils/supplierApprovalHelpers";
import moment from "moment";
import { SupplierPageHeader, SupplierSection, SupplierStatusChip, supplierUi } from "../../shared/supplierUi";

export const suppliers_atrr = {
	basic_details: "Basic Details",
	address_details: "Address Details",
	financial_details: "Financial Details",
	files: "Files",
	compliance_certificates: "Compliance Certificates",
	contact_details: "Contact Details",
	vendor_agreement: "Vendor Agreements",
};
export const factory_atrr = {
	basic_details: "Basic Details",
	address_details: "Address Details",
	files: "Files",
	compliance_certificates: "Compliance Certificates",
	contact_details: "Contact Details",
};

const stepLabel = (row) => {
	if (row?.action === APPROVAL_ACTIONS.UNAPPROVE) return "Unapproved";
	if (row?.action === APPROVAL_ACTIONS.OPEN_PENDING) return "Reopened";
	if (row?.action === APPROVAL_ACTIONS.APPROVE || row?.approved) return "Approved";
	if (row?.action === APPROVAL_ACTIONS.REJECT) return "Rejected";
	// Historical rows without action
	if (row?.remark) return "Rejected";
	return "Pending";
};

const StatusCard = ({ title, latest, onClick }) => {
	const status = getApprovalStatus(latest);
	return (
		<Box
			onClick={onClick}
			sx={{
				p: 1.75,
				border: `1px solid ${supplierUi.border}`,
				borderRadius: supplierUi.radius,
				boxShadow: supplierUi.shadow,
				bgcolor: "#fff",
				cursor: "pointer",
				transition: "border-color 0.15s ease, background-color 0.15s ease",
				"&:hover": { backgroundColor: grey[50], borderColor: supplierUi.borderStrong },
			}}
			display="flex"
			width="inherit"
			alignItems="center"
			gap={2}>
			<Stack flex={1} minWidth={0}>
				<Typography variant="body1" fontSize={17} fontWeight={600} width="100%" color={supplierUi.textPrimary}>
					{title}
				</Typography>
				{latest?.remark ? (
					<Typography
						variant="body2"
						fontSize={14}
						width="100%"
						color={status === "Rejected" ? "error" : "text.secondary"}
						noWrap>
						{`Remark: ${latest.remark}`}
					</Typography>
				) : null}
				<Typography variant="caption" sx={{ fontSize: 13 }} color="text.secondary">
					Click to view history
				</Typography>
			</Stack>
			<SupplierStatusChip label={status} />
		</Box>
	);
};

const HistoryDialog = ({ open, onClose, title, history }) => {
	const [expandedId, setExpandedId] = useState(null);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				{title} — History
				<IconButton onClick={onClose}>
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{!history?.length ? (
					<Typography>No approval history yet.</Typography>
				) : (
					<Stack spacing={2} sx={{ py: 1 }}>
						{history.map((row, index) => {
							const label = stepLabel(row);
							const snapshot =
								typeof row.data_snapshot === "string"
									? (() => {
											try {
												return JSON.parse(row.data_snapshot);
											} catch {
												return row.data_snapshot;
											}
									  })()
									: row.data_snapshot;
							const hasSnapshot = !!snapshot && label === "Approved";
							return (
								<Box
									key={row.id || index}
									sx={{
										borderLeft: `3px solid ${
											label === "Approved" ? "#027A48" : label === "Rejected" ? "#B42318" : grey[400]
										}`,
										pl: 2,
									}}>
									<Typography fontWeight={700}>
										Step {index + 1}: {label}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{moment(row.createdAt || row.created_at || row.approved_date).isValid()
											? moment(row.createdAt || row.created_at || row.approved_date).format("DD MMM YYYY, HH:mm")
											: "-"}
										{row.updated_by ? ` · ${row.updated_by}` : ""}
									</Typography>
									{row.remark ? (
										<Typography
											variant="body2"
											color={label === "Rejected" ? "error" : "text.secondary"}
											sx={{ mt: 0.5 }}>
											Remark: {row.remark}
										</Typography>
									) : null}
									{hasSnapshot ? (
										<>
											<Typography
												variant="body2"
												sx={{ mt: 0.5, cursor: "pointer", textDecoration: "underline" }}
												onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}>
												{expandedId === row.id ? "Hide approved data" : "View approved data"}
											</Typography>
											<Collapse in={expandedId === row.id}>
												<Box
													component="pre"
													sx={{
														mt: 1,
														p: 1.5,
														background: grey[100],
														borderRadius: 1,
														overflow: "auto",
														maxHeight: 280,
														fontSize: 12,
													}}>
													{JSON.stringify(snapshot, null, 2)}
												</Box>
											</Collapse>
										</>
									) : null}
								</Box>
							);
						})}
					</Stack>
				)}
			</DialogContent>
		</Dialog>
	);
};

const SupplierStatus = ({ companyData, allFactories, hidePageHeader = false }) => {
	const [historyOpen, setHistoryOpen] = useState(false);
	const [historyTitle, setHistoryTitle] = useState("");
	const [historyRows, setHistoryRows] = useState([]);

	const companySections = useMemo(() => {
		return COMPANY_APPROVAL_TYPES.map((type) => ({
			type,
			nameType: suppliers_atrr[type],
			latest: getLatestApproval(companyData?.supplierApproval, type),
			history: getApprovalHistory(companyData?.supplierApproval, type),
		}));
	}, [companyData]);

	const factorySections = useMemo(() => {
		return (allFactories || []).map((fact) => ({
			name: fact.name,
			sections: FACTORY_APPROVAL_TYPES.map((type) => ({
				type,
				nameType: factory_atrr[type],
				latest: getLatestApproval(fact?.factoryApproval, type),
				history: getApprovalHistory(fact?.factoryApproval, type),
			})),
		}));
	}, [allFactories]);

	const openHistory = (title, history) => {
		setHistoryTitle(title);
		setHistoryRows(history || []);
		setHistoryOpen(true);
	};

	return (
		<Stack width="100%" spacing={2.5}>
			{!hidePageHeader && (
				<SupplierPageHeader title="Approval Status" subtitle="Section-wise approval progress for company and factories" />
			)}
			<SupplierSection title="Company Info">
				<Stack spacing={1.25}>
					{companySections.map((val) => (
						<StatusCard
							key={val.type}
							title={val.nameType}
							latest={val.latest}
							onClick={() => openHistory(`Company · ${val.nameType}`, val.history)}
						/>
					))}
				</Stack>
			</SupplierSection>
			{factorySections.map((fact) => (
				<SupplierSection key={fact.name} title={`Factory · ${fact.name}`}>
					<Stack spacing={1.25}>
						{fact.sections.map((val) => (
							<StatusCard
								key={`${fact.name}-${val.type}`}
								title={val.nameType}
								latest={val.latest}
								onClick={() => openHistory(`${fact.name} · ${val.nameType}`, val.history)}
							/>
						))}
					</Stack>
				</SupplierSection>
			))}
			<HistoryDialog
				open={historyOpen}
				onClose={() => setHistoryOpen(false)}
				title={historyTitle}
				history={historyRows}
			/>
		</Stack>
	);
};

export default SupplierStatus;
